/* eslint-disable react-hooks/exhaustive-deps */
import { v4 as uuid4 } from 'uuid';
import debounce from 'debounce';
import {
  createOrderFile,
  deleteOrderArchive,
  deleteOrderFile,
  TemplateFileData,
  updateOrderFile,
  updateOrderFileData,
} from 'graphql/documents/mutations';
import {
  getOrderDocuments,
  getTemplatesResponse,
  GQLGetOrderDocuments,
  GQLGetTemplates,
} from 'graphql/documents/queries';
import { useDocumentUpdateSocketEvent } from 'hooks/useDocumentUpdateFromSocket';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { capitalize } from 'utils/helpers';
import { getTokens } from 'utils/search';
import { useWordHighlight } from 'v2-hooks/useWordHighlight';
import { DocumentsType } from 'v2-types/order';
import { useModalManager } from './hooks/useModalState';

import { DocumentFile, OrderDocumentsType, SearchParams } from './types';
import { mapEstatesToAssociations, mapPartiesToAssociations } from './utils';
import { searchDocuments } from './utils/filterDocuments';
import { orderDocumentsAlphabetically } from './utils/orderDocumentsAlphabetically';
import { useDropboxsignEvent } from 'hooks/useDropboxsignEvent';

export type KindTabType = {
  kind: string;
  label: string;
};

const useDocuments = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const showAlert = useAlert();

  const [openBackdrop, setOpenBackdrop] = useState(false);
  const searchParamsRef = useRef<SearchParams>({
    query: '',
    party: '',
    property: '',
  });
  const queryInputRef = useRef<HTMLInputElement>();
  const [regexSearchWords, setRegexSearchWords] = useState<string>('');

  const htmlDocumentsContainerRef = useRef<HTMLDivElement | null>(null);
  const { searchWordClass } = useWordHighlight({
    container: htmlDocumentsContainerRef.current,
    regexSearchWords,
  });

  const [orderDocuments, setOrderDocuments] = useState<OrderDocumentsType>({
    files: [],
    parties: [],
    estates: [],
  });
  const documentsRef = useRef<DocumentsType[]>();

  const [showAlerUpdate, setShowAlertUpdate] = useState(false);
  const [kindTabs, setKindTabs] = useState<KindTabType[]>([]);
  const [currentKindTabSelected, setCurrentKindTabSelected] = useState<string>('');
  const currentKindTabRef = useRef<string>('');
  const [selectedTabDocuments, setSelectedTabDocuments] = useState<DocumentsType[]>([]);

  const debounceSearch = useCallback(
    debounce(() => {
      if (!documentsRef.current) return;

      const activeTab = currentKindTabRef.current;
      setOpenBackdrop(true);
      const { query, party, property } = searchParamsRef.current;
      if (query.length <= 0 && party.length <= 0 && property.length <= 0) {
        setOpenBackdrop(false);
        setRegexSearchWords('');
        setSelectedTabDocuments(
          documentsRef.current.filter((file) => file.kind === activeTab)
        );
        return;
      }

      const { queryTokens, partyTokens, propertyTokens } = getTokens({
        query,
        party,
        property,
      });

      let documents: DocumentsType[] = [];
      let searchWords = '';
      if (documentsRef.current) {
        documents = searchDocuments({
          documents: documentsRef.current.filter((file) => file.kind === activeTab),
          queryTokens,
          partyTokens: partyTokens ? [partyTokens.join(' ')] : undefined,
          propertyTokens,
        });

        const words = [
          ...(queryTokens || []),
          ...(partyTokens || []),
          ...(propertyTokens || []),
        ].join('|');

        searchWords = `(${words})`;
      }

      setOpenBackdrop(false);
      setSelectedTabDocuments(orderDocumentsAlphabetically(documents));
      setRegexSearchWords(searchWords);
    }, 1000),
    []
  );

  function getKindTabLabel(kind: string) {
    return capitalize(kind);
  }

  const [tableHeaders, setTableHeaders] = useState<{ id: string; label: string }[]>([]);
  const { modalState, modalActions } = useModalManager();

  function getTableHeadersByKind(kind: string) {
    if (kind === 'accounting' || kind === 'pdf' || kind === 'zip') {
      return [
        {
          id: uuid4(),
          label: 'Name',
        },
        {
          id: uuid4(),
          label: 'Last Update',
        },
        {
          id: uuid4(),
          label: 'Size',
        },
        {
          id: uuid4(),
          label: 'Options',
        },
      ];
    }
    return [];
  }

  const onSelectKindTab = (kind: string) => {
    debounceSearch.clear();
    currentKindTabRef.current = kind;
    setCurrentKindTabSelected(kind);
    setSelectedTabDocuments(orderDocuments.files.filter((file) => file.kind === kind));
    setTableHeaders(getTableHeadersByKind(kind));
    searchParamsRef.current = { query: '', party: '', property: '' };
    if (queryInputRef.current) {
      queryInputRef.current.value = '';
    }
  };

  const updateOrderDocumentsAndCloseModal = (documents: DocumentsType[]) => {
    const files = orderDocumentsAlphabetically(documents);
    setOrderDocuments((prevData) => ({
      ...prevData,
      files,
    }));

    setSelectedTabDocuments(files.filter((file) => file.kind === currentKindTabSelected));
    modalActions.closeModal();
  };

  const { mutate: createFile } = useMutation(createOrderFile, {
    onSuccess: (data) => {
      updateOrderDocumentsAndCloseModal(data?.createOrderFile.files ?? []);

      documentsRef.current = data.createOrderFile.files;
      debounceSearch();

      showAlert('Successful document creation', 'success');
      setOpenBackdrop(false);
    },
    onError: () => {
      setOpenBackdrop(false);
    },
  });

  const { mutate: updateFile, isLoading: isUpdateFileLoading } = useMutation(updateOrderFile, {
    onSuccess: (data) => {
      documentsRef.current = data.updateOrderFile.files;
      debounceSearch();

      updateOrderDocumentsAndCloseModal(data?.updateOrderFile.files ?? []);

      showAlert('Document update successful', 'success');
      setOpenBackdrop(false);
    },
    onError: () => {
      setOpenBackdrop(false);
    },
  });

  const { mutate: deleteDocumentMutation, isLoading: isDeleteDocumentLoading } = useMutation(
    deleteOrderFile,
    {
      onSuccess: (data) => {
        documentsRef.current = data.deleteOrderFile.files;
        debounceSearch();

        updateOrderDocumentsAndCloseModal(data?.deleteOrderFile.files ?? []);

        showAlert('Document deletion successful', 'success');
        setOpenBackdrop(false);
      },
      onError: () => {
        setOpenBackdrop(false);
      },
    }
  );

  const { mutate: deleteArchive } = useMutation(deleteOrderArchive, {
    onSuccess: (data) => {
      documentsRef.current = data.deleteOrderArchive.files;
      debounceSearch();

      updateOrderDocumentsAndCloseModal(data?.deleteOrderArchive.files ?? []);

      showAlert('File was successfully deleted', 'success');
      setOpenBackdrop(false);
    },
    onError: () => {
      setOpenBackdrop(false);
    },
  });

  const { isLoading: isGetOrderDocumentsLoading, isFetching: isGetOrderDocumentFetching } =
    useQuery<GQLGetOrderDocuments>(
      ['order-documents', orderId],
      () => getOrderDocuments({ id: orderId }),
      {
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
          const kinds = ['upload', 'accounting', 'auto', 'package', 'template', 'zip', 'pdf'];

          const tabs: KindTabType[] = kinds
            .filter((kind) => data.getOrder.files.some((file) => file.kind === kind))
            .map((kind) => ({
              kind,
              label: getKindTabLabel(kind),
            }))
            .concat([
              {
                kind: 'sign',
                label: 'Signature',
              },
            ])
            .sort((a, b) => a.kind.localeCompare(b.kind));

          const documents = {
            files: orderDocumentsAlphabetically(data?.getOrder.files ?? []),
            estates: mapEstatesToAssociations(data?.getOrder.estates ?? []),
            parties: mapPartiesToAssociations(data?.getOrder.parties ?? []),
          };

          if (currentKindTabSelected === '' && tabs.some((tab) => tab.kind === 'auto')) {
            currentKindTabRef.current = 'auto';
            setCurrentKindTabSelected('auto');
            setSelectedTabDocuments(documents.files.filter((file) => file.kind === 'auto'));
            setTableHeaders(getTableHeadersByKind('auto'));
          } else if (currentKindTabSelected !== '') {
            setSelectedTabDocuments(documents.files.filter((file) => file.kind === currentKindTabSelected));
          }

          setKindTabs(tabs);

          setOrderDocuments(documents);

          if (showAlerUpdate) {
            setShowAlertUpdate(false);
          }
          documentsRef.current = data.getOrder.files ?? [];
        },
      }
    );

  const queryCache = useQueryClient();
  useDocumentUpdateSocketEvent({
    callback: () => {
      queryCache.invalidateQueries(['order-documents', orderId]);
    },
  });

  const templateDocsResponse = useQuery<GQLGetTemplates>(
    ['templates'],
    () => getTemplatesResponse(),
    { refetchOnWindowFocus: false }
  );

  const templates = (templateDocsResponse.data?.getTemplates ?? []).sort((templateA, templateB) =>
    templateA.description.localeCompare(templateB.description)
  );

  const { mutate: updateTemplateFileData, isLoading: isUpdateTemplateFileDataLoading } =
    useMutation(updateOrderFileData, {
      onSuccess: (data) => {
        documentsRef.current = data.updateOrderFileData.files;
        debounceSearch();

        updateOrderDocumentsAndCloseModal(data.updateOrderFileData.files);

        showAlert('Successful template data created.', 'success');
        setOpenBackdrop(false);
      },
      onError: () => {
        setOpenBackdrop(false);
      },
    });

  const handleCreateFile = (payload) => {
    setOpenBackdrop(true);
    createFile({
      ...payload,
      id: orderId,
    });
  };

  const allFilesinDocuments: DocumentFile[] = useMemo(
    () =>
      orderDocuments.files.flatMap((document) =>
        document.archives.flatMap(({ files }) =>
          files
            .filter(({ data }) => data)
            .map(({ filename, source, data, _id }) => ({
              _id,
              filename,
              source,
              description: document.description,
              data,
              kind: document.kind,
            }))
        )
      ),
    [orderDocuments.files]
  );

  const [signTabQuery, setSignTabQuery] = useState<string>('');
  const onSearch = ({ value, type = 'query' }: { value: string; type: keyof SearchParams }) => {
    if (currentKindTabSelected === 'sign') {
      setSignTabQuery(value);
      return;
    }

    searchParamsRef.current[type] = value;
    debounceSearch.clear();
    debounceSearch();
  };

  const toggleLockDocument = () => {
    if (modalState.type !== 'toggleDocumentLock') return;

    setOpenBackdrop(true);
    updateFile({
      id: orderId,
      fileId: modalState.documentId,
      locked: modalState.lock,
    });
  };

  const updatePdfZipParts = (bundle: string[]) => {
    if (modalState.type !== 'editBundleZipPdfContent') return;

    setOpenBackdrop(true);
    updateFile({
      id: orderId,
      fileId: modalState.document?._id!,
      parts: bundle,
    });
  };

  const deleteDocument = () => {
    if (!['deleteDocumentConfirmation', 'deleteFileConfirmation'].includes(modalState.type)) return;

    setOpenBackdrop(true);
    const isDocument = modalState.type === 'deleteDocumentConfirmation';
    if (isDocument) {
      deleteDocumentMutation({
        id: orderId,
        fileId: modalState.document?._id!,
      });
    } else if (modalState.type === 'deleteFileConfirmation') {
      deleteArchive({
        id: orderId,
        documentId: modalState.file?.documentId!,
        archiveId: modalState.file?.partyId!,
        fileId: modalState.file?.fileId!,
      });
    }
  };

  const updateTemplateData = (data: TemplateFileData[]) => {
    if (modalState.type !== 'editTemplateData' && modalState.type !== 'editExpectedFrom') return;

    updateTemplateFileData({
      id: orderId,
      fileId: modalState.document?._id!,
      data,
    });
  };

  const isDocumentsLoading = isGetOrderDocumentsLoading || isGetOrderDocumentFetching;

  useDropboxsignEvent({
    callback: () => {
      queryCache.invalidateQueries(['getSignatures', orderId]);
    },
  });

  return {
    queryInputRef,
    isDocumentsLoading,
    modalActions,
    updateTemplateData,
    isUpdateTemplateFileDataLoading,
    deleteDocument,
    isDeleteDocumentLoading,
    updatePdfZipParts,
    toggleLockDocument,
    isUpdateFileLoading,
    tableHeaders,
    onSelectKindTab,
    selectedTabDocuments,
    kindTabs,
    currentKindTabSelected,
    documents: orderDocuments.files,
    estates: orderDocuments.estates,
    parties: orderDocuments.parties,
    orderId,
    handleCreateFile,
    templates,
    openBackdrop,
    allFilesinDocuments,
    searchParamsRef,
    onSearch,
    htmlDocumentsContainerRef,
    searchWordClass,
    showAlerUpdate,
    modalState,
    signTabQuery,
  };
};

export default useDocuments;
